"use client";

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useMemo,
  useCallback
} from "react";
import { Sport, SportName } from "@/types/sports";
import { sportService } from "@/lib/services/sportService";

interface SportContextType {
  activeSport: SportName | null;
  switchSport: (sport: SportName) => void;
  allSports: Sport[];
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

const SportContext = createContext<SportContextType | undefined>(undefined);

export function SportProvider({ children }: { children: ReactNode }) {
  const [allSports, setAllSports] = useState<Sport[]>([]);
  const [activeSport, setActiveSport] = useState<SportName | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Clear error helper function
  const clearError = useCallback(() => setError(null), []);

  // Fetch sports list on mount
  useEffect(() => {
    let isMounted = true;

    const fetchSports = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await sportService.getAllSports();
        if (!isMounted) return;
        
        if (response.success) {
          // Ensure response.data is an array before using array methods
          const sportsData = Array.isArray(response.data) ? response.data : [];
          setAllSports(sportsData);
          // Set default active sport (one marked as active or first in list)
          const defaultSport = sportsData.find((s) => s.active) || sportsData[0] || null;
          setActiveSport(defaultSport?.name || null);
        } else {
          setError(response.error || "Failed to fetch sports");
        }
      } catch (err) {
        if (isMounted) {
          setError("An unexpected error occurred");
          console.error("Error fetching sports:", err);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchSports();

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array means this runs once on mount

  const switchSport = useCallback((sport: SportName) => {
    setActiveSport(sport);
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      activeSport,
      switchSport,
      allSports,
      isLoading,
      error,
      clearError
    }),
    [
      activeSport,
      switchSport,
      allSports,
      isLoading,
      error,
      clearError
    ]
  );

  return (
    <SportContext.Provider value={contextValue}>
      {children}
    </SportContext.Provider>
  );
}

export const useSport = (): SportContextType => {
  const context = useContext(SportContext);
  if (context === undefined) {
    throw new Error("useSport must be used within a SportProvider");
  }
  return context;
};
